import jsPDF from 'jspdf';

import { generateColumnDefs } from 'components/practice/BeyondBilling/Reports/components/ReportView/lib/generateColumnDefs';

import { mString } from '../utility';

import extractDataFromColDefs from './lib/extractDataFromColDefs';
import { generateCompleteTable, generateTable } from './lib/generateCompleteTable';
import { getAgGridData } from './lib/getAgGridData';
import { filterColumns, sumByColumn } from './lib/helpers';
import { printPrintWindow } from './lib/printPrintWindow';
import { generateFooter } from './pdfFooter';
import { generateHeader } from './pdfHeader';

const isIdColumn = (field) => {
  return field.toString().toLowerCase() === 'id' || field.toString().includes('Id');
};

const getTotalRows = (statusPanelData) => {
  return statusPanelData.map(({ field, label, value }) => [
    label,
    field.endsWith('_') ? value : mString(value)
  ]);
};

//////////////////////////////////////////// --- AG GRID TABLE
//get the data from the ag-grid
const getRowsAndTotals = ({ gridApi, statusPanelData }) => {
  const totals = [];

  const { rows, data, columns } = getAgGridData(gridApi);

  const mappedColumns = Object.fromEntries(columns.map((col) => [col.field, col]));

  if (statusPanelData) {
    rows.forEach((row) => {
      totals.push(
        statusPanelData.map(({ field }) => {
          if (isIdColumn(field)) {
            return '';
          }

          const column = mappedColumns[field];
          const value = row[field];

          if (!isNaN(Number(value)) && (column?.valueFormatter || column?.valueGetter)) {
            return value;
          }

          return '';
        })
      );
    });
  }

  const exportRows = data.map((row) => {
    return columns.map((col) => {
      return { content: row[col.field], strike: row.hasStrike };
    });
  });

  return { rows: exportRows, columns, totals };
};

//print the ag-grid table
export const printAgGridTable = ({
  hasMultiple = false,
  statusPanelData = false,
  gridApi: proppedGridApi,
  headerConfigs: proppedHeaderConfigs = {},
  footerConfigs: proppedFooterConfigs = {}
}) => {
  if (!proppedGridApi) return;

  const doc = new jsPDF();
  let totalsSum;

  const createGridTablePdf = (
    gridApi = proppedGridApi,
    headerConfigs = proppedHeaderConfigs,
    footerConfigs = proppedFooterConfigs
  ) => {
    const { rows, totals, columns } = getRowsAndTotals({ gridApi, statusPanelData });

    const columnHeaders = columns.map((col) => col.headerName);

    if (statusPanelData) {
      if (statusPanelData?.[0]?.value) {
        totalsSum = getTotalRows(statusPanelData);
      } else {
        const sumOfColumns = sumByColumn(totals);

        totalsSum = statusPanelData.map(({ label }, index) => [label, sumOfColumns[index]]);
      }
    }

    generateCompleteTable({
      doc,
      columnHeaders,
      rows,
      totalsSum,
      headerConfigs,
      footerConfigs
    });
  };

  if (!hasMultiple) {
    createGridTablePdf();
  } else {
    // this case is used in preview multiple invoices
    const gridApiEntries = Object.entries(proppedGridApi);

    gridApiEntries.forEach(([key, value], index) => {
      createGridTablePdf(value, proppedHeaderConfigs?.[key], proppedFooterConfigs?.[key]);

      //add page if not last page
      if (index < gridApiEntries.length - 1) {
        doc.addPage();
      }
    });
  }

  printPrintWindow(doc);
};
//////////////////////////////////////////// ^^^ AG GRID TABLE

//////////////////////////////////////////// --- CUSTOM TABLE

// recursively add table for each row if it has children
const addNestedTable = ({ doc, data = [], startY = 0, columns, isNested = false }) => {
  if (!data.length) return;

  let updatedStartY = startY;

  const finalColumns = columns || generateColumnDefs(data);
  const formattedData = extractDataFromColDefs(data, finalColumns);
  const rows = formattedData.map((row) => finalColumns.map((col) => row[col.field]));

  const handleGenerateTable = (y = startY, rows2 = rows) => {
    return generateTable({
      doc,
      columnHeaders: finalColumns.map((col) => col.headerName),
      rows: rows2,
      startY: y,
      configs: { ...(isNested && { headStyles: { fillColor: 'gray' } }) }
    });
  };

  if (!data?.[0]?.children) {
    return handleGenerateTable();
  }

  data.forEach((record, index) => {
    updatedStartY = handleGenerateTable(updatedStartY, [rows[index]]);

    if (record?.children?.length) {
      updatedStartY = addNestedTable({
        doc,
        data: record?.children,
        startY: updatedStartY,
        isNested: true
      });
    }
  });

  return updatedStartY;
};

//print table when data are collapsed
const printCollapsedTable = ({
  doc,
  tableData,
  columns,
  statusPanelData,
  headerConfigs,
  footerConfigs
}) => {
  let totalsSum;

  const formattedData = extractDataFromColDefs(tableData, columns);

  const rows = formattedData.map((row) => columns.map((col) => row[col.field]));
  const columnHeaders = columns.map((col) => col.headerName);

  if (statusPanelData) {
    // if totals has a value by itself then display that value
    if (statusPanelData?.[0]?.value) {
      totalsSum = getTotalRows(statusPanelData);
    } else {
      const totals = tableData.map((row) => {
        return statusPanelData.map((col) => {
          const value = row[col.field];

          return !col.field.endsWith('_') && !isNaN(Number(value)) ? value : '';
        });
      });

      const sumOfColumns = sumByColumn(totals);

      totalsSum = statusPanelData.map(({ label }, index) => [label, sumOfColumns[index]]);
    }
  }

  generateCompleteTable({ doc, columnHeaders, rows, totalsSum, headerConfigs, footerConfigs });
};

//print table when data are expanded
const printExpandedTable = ({ doc, tableData, columns, headerConfigs, footerConfigs }) => {
  const startY = generateHeader({ doc, headerConfigs });

  const endY = addNestedTable({ doc, data: tableData, columns, startY });

  generateFooter({ doc, endY, headerConfigs, footerConfigs });
};

//print the table which is not ag-grid
export const printTable = ({
  tableData,
  columns: proppedColumns,
  statusPanelData = false,
  printExpandedRows = false,
  headerConfigs = {},
  footerConfigs = {}
}) => {
  if (!tableData || !proppedColumns) return;

  const doc = new jsPDF();

  const columns = filterColumns(proppedColumns);

  if (printExpandedRows) {
    printExpandedTable({ doc, tableData, columns, statusPanelData, headerConfigs, footerConfigs });
  } else {
    printCollapsedTable({
      doc,
      tableData,
      columns,
      statusPanelData,
      headerConfigs,
      footerConfigs
    });
  }

  printPrintWindow(doc);
};
